import { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { refundOrderPayment } from "services/orders/rest";
import { PriceDto } from "services/orders/restModels";
import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useOrder } from "shared/hooks/useOrder";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { useNotificationSnackbar } from "shared/ui";

import { ordersListLocation } from "features/Orders/lib/lastOrdersSearch";

import { OrderDetailsState } from "features/OrderDetails/types";

export const useOrderDetailsState = (): OrderDetailsState => {
    const locale = useLocale();
    const history = useHistory();
    const { orderId } = useParams<{ orderId: string }>();

    // Кривой id в адресе — null: хук на нём отдаёт ошибку, и экран показывает её же.
    const parsedOrderId = Number(orderId);
    const orderIdFromRoute = isNaN(parsedOrderId) ? null : parsedOrderId;

    const { order, loading, error, setOrder } = useOrder(orderIdFromRoute);
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();

    const [refunding, setRefunding] = useState<boolean>(false);
    const [isRefundDialogOpen, setRefundDialogOpen] = useState<boolean>(false);

    const notify = useNotificationSnackbar();

    const currencies = constants?.currencies;

    const formatPrice = useCallback(
        (price: PriceDto) => {
            const currency = (currencies || []).find((item) => item.id === price.currencyId) || null;

            return createCurrencyFormatter(currency, locale).toShow(price.price);
        },
        [currencies, locale]
    );

    const openRefundDialog = useCallback(() => setRefundDialogOpen(true), []);

    const closeRefundDialog = useCallback(() => setRefundDialogOpen(false), []);

    const confirmRefund = useCallback(async () => {
        if (order === null) return;

        setRefunding(true);

        try {
            const updated = await refundOrderPayment(order.id);

            if (updated) {
                // Ручка отдаёт заказ целиком — второго запроса не нужно.
                setOrder(updated);
            }

            setRefundDialogOpen(false);
            notify.showSuccess("Возврат платежа выполнен, деньги поступят на счёт гостя");
        } catch (requestError) {
            // Причину отказа бэк присылает текстом (эквайринг отказал, не настроена касса) —
            // showError достаёт её из ошибки сам.
            notify.showError("Не удалось вернуть деньги", requestError);
        } finally {
            setRefunding(false);
        }
    }, [notify, order, setOrder]);

    // Возвращаемся на список с теми же фильтрами: они лежат в его query (см. lastOrdersSearch).
    const goBack = useCallback(() => history.push(ordersListLocation()), [history]);

    return {
        order,
        loading: loading || constantsLoading,
        error: error || constantsError,

        refunding,
        isRefundDialogOpen,
        openRefundDialog,
        closeRefundDialog,
        confirmRefund,

        goBack,

        locale,
        formatPrice,

        notify,
    };
};
